import { Form } from "antd";
import * as React from "react";
import {
  IOnboardingStep,
  IOnboardingTooltipItem,
  ONBOARDING_ICON_OPTIONS,
  OnboardingIconPosition,
  OnboardingIconType,
  OnboardingTooltipPosition,
  OnboardingTooltipTriggerType
} from "../../../../models/onboarding-tooltip";
import OnboardingTooltipService from "../../../../services/onboarding-tooltip-service";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { AnimatedIconType, OnboardingAnimatedIcon } from "../../../onboarding-tooltip/onboarding-animated-icon";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaColorPicker from "../../../ui-kit/kuika-color-picker/kuika-color-picker";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOnboardingTooltip from "../../../ui-kit/kuika-onboarding-tooltip/kuika-onboarding-tooltip";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaMediumText from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./onboarding-tooltip-modal.scss";

export interface IOnboardingTooltipModalProps {
  isOpen: boolean;
  onClose: () => void;
  tooltip?: IOnboardingTooltipItem;
  folderId?: string;
  refreshOnboardingTooltips?: () => void;
}

interface IFormValues {
  name: string;
  selector: string;
  useCssSelector: boolean;
  label: string;
  header: string;
  image: string;
  steps: IOnboardingStep[];
  learnMoreUrl: string;
  position: OnboardingTooltipPosition;
  triggerType: OnboardingTooltipTriggerType;
  backgroundColor: string;
  textColor: string;
  width: string;
  height: string;
  icon: OnboardingIconType | "";
  iconColor: string;
  iconSize: string;
  iconPosition: OnboardingIconPosition | "";
  tooltipOffsetX: string;
  tooltipOffsetY: string;
  iconOffsetX: string;
  iconOffsetY: string;
  isDraft: boolean;

  showStepsEditor: boolean;
}

const IconPositionSelector: React.FC<{
  value?: OnboardingIconPosition | "";
  onChange: (value: OnboardingIconPosition) => void;
}> = ({ value, onChange }) => {
  const positions: OnboardingIconPosition[] = [
    "top-start",
    "top-center",
    "top-end",
    "right-start",
    "right-center",
    "right-end",
    "bottom-start",
    "bottom-center",
    "bottom-end",
    "left-start",
    "left-center",
    "left-end"
  ];

  return (
    <div className="onboarding-icon-position-selector">
      <KuikaSmallText>Icon Positioning</KuikaSmallText>
      <div className="onboarding-icon-position-selector__container">
        <div className="onboarding-icon-position-selector__target">
          <span>Target element</span>
        </div>
        {positions.map((pos) => (
          <div
            key={pos}
            className={`onboarding-icon-position-selector__dot onboarding-icon-position-selector__dot--${pos} ${
              value === pos ? "onboarding-icon-position-selector__dot--active" : ""
            }`}
            onClick={() => onChange(pos)}
            title={pos.replace("-", " ")}
          />
        ))}
      </div>
    </div>
  );
};

const OnboardingTooltipModalContent: React.FC<{
  form: any;
  tooltip?: IOnboardingTooltipItem;
  onClose: () => void;
  folderId?: string;
  refreshOnboardingTooltips?: () => void;
  formValuesRef: React.MutableRefObject<IFormValues | null>;
}> = ({ form, tooltip, onClose, folderId, refreshOnboardingTooltips, formValuesRef }) => {
  const [formValues, setFormValues] = React.useState<IFormValues>(() => ({
    name: tooltip?.name || "",
    selector: tooltip?.selector || "",
    useCssSelector: tooltip?.useCssSelector || false,
    label: tooltip?.label || "",
    header: tooltip?.header || "",
    image: tooltip?.image || "",
    steps: tooltip?.steps || [],
    learnMoreUrl: tooltip?.learnMoreUrl || "",
    position: tooltip?.position || "bottom",
    triggerType: tooltip?.triggerType || "hover",
    backgroundColor: tooltip?.backgroundColor || "",
    textColor: tooltip?.textColor || "",
    width: tooltip?.width?.toString() || "",
    height: tooltip?.height?.toString() || "",
    icon: tooltip?.icon || "",
    iconColor: tooltip?.iconColor || "",
    iconSize: tooltip?.iconSize?.toString() || "",
    iconPosition: tooltip?.iconPosition || "right-center",
    tooltipOffsetX: tooltip?.tooltipOffsetX?.toString() || "",
    tooltipOffsetY: tooltip?.tooltipOffsetY?.toString() || "",
    iconOffsetX: tooltip?.iconOffsetX?.toString() || "",
    iconOffsetY: tooltip?.iconOffsetY?.toString() || "",
    isDraft: tooltip?.isDraft ?? true,
    showStepsEditor: (tooltip?.steps?.length ?? 0) > 0
  }));

  React.useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues, formValuesRef]);

  const updateFormValue = React.useCallback(
    (field: keyof IFormValues, value: any) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
      form.setFieldValue(field, value);
    },
    [form]
  );

  const {
    showStepsEditor,
    steps,
    selector,
    useCssSelector,
    icon,
    iconColor,
    iconSize,
    iconPosition,
    label,
    header,
    image,
    learnMoreUrl,
    position,
    triggerType,
    backgroundColor,
    textColor,
    width,
    height
  } = formValues;

  const isEdit = !!tooltip;

  const handleToggleStepsEditor = React.useCallback(() => {
    const newValue = !formValues.showStepsEditor;
    const newSteps = newValue ? formValues.steps : [];
    setFormValues((prev) => ({ ...prev, showStepsEditor: newValue, steps: newSteps }));
    form.setFieldsValue({ showStepsEditor: newValue, steps: newSteps });
  }, [formValues.showStepsEditor, formValues.steps, form]);

  const handleAddStep = React.useCallback(() => {
    const newSteps = [...formValues.steps, { label: "", header: "", image: "", selector: "" }];
    updateFormValue("steps", newSteps);
  }, [formValues.steps, updateFormValue]);

  const handleRemoveStep = React.useCallback(
    (index: number) => {
      const newSteps = formValues.steps.filter((_, i) => i !== index);
      updateFormValue("steps", newSteps);
    },
    [formValues.steps, updateFormValue]
  );

  const handleMoveStep = React.useCallback(
    (index: number, direction: "up" | "down") => {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= formValues.steps.length) return;

      const newSteps = [...formValues.steps];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      updateFormValue("steps", newSteps);
    },
    [formValues.steps, updateFormValue]
  );

  const handleImageUpload = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        alert("Image size must be less than 5MB");
        e.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        updateFormValue("image", base64String);
        e.target.value = "";
      };
      reader.onerror = () => {
        alert("Failed to read image file");
        e.target.value = "";
      };
      reader.readAsDataURL(file);
    },
    [updateFormValue]
  );

  const handleStepImageUpload = React.useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        alert("Image size must be less than 5MB");
        e.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const updatedSteps = formValues.steps.map((s: IOnboardingStep, i: number) =>
          i === index ? { ...s, image: base64String } : s
        );
        updateFormValue("steps", updatedSteps);
        e.target.value = "";
      };
      reader.onerror = () => {
        alert("Failed to read image file");
        e.target.value = "";
      };
      reader.readAsDataURL(file);
    },
    [formValues.steps, updateFormValue]
  );

  const getImageSrc = (imageData?: string): string | undefined => {
    if (!imageData) return undefined;
    if (imageData.startsWith("data:image/")) return imageData;
    if (imageData.startsWith("http://") || imageData.startsWith("https://")) return imageData;
    return `data:image/png;base64,${imageData}`;
  };

  const renderStepEditor = (step: IOnboardingStep, index: number) => {
    const isFirst = index === 0;
    const isLast = index === steps.length - 1;
    const isSelectorDisabled = isFirst && !!selector;

    return (
      <div key={index} className="onboarding-tooltip-modal__step">
        <div className="onboarding-tooltip-modal__step-header">
          <KuikaSmallMediumText>Step {index + 1}</KuikaSmallMediumText>
          <div className="onboarding-tooltip-modal__step-actions">
            <KuikaButton
              type="text"
              isIcon
              disabled={isFirst}
              onClick={() => handleMoveStep(index, "up")}
              aria-label="Move step up"
              tabIndex={0}
            >
              <KuikaSvg name="chevron-up" type="arrows" width={14} height={14} disabled={isFirst} />
            </KuikaButton>
            <KuikaButton
              type="text"
              isIcon
              disabled={isLast}
              onClick={() => handleMoveStep(index, "down")}
              aria-label="Move step down"
              tabIndex={0}
            >
              <KuikaSvg name="chevron-down" type="arrows" width={14} height={14} disabled={isLast} />
            </KuikaButton>
            <KuikaButton
              type="text"
              isIcon
              color="danger"
              onClick={() => handleRemoveStep(index)}
              aria-label="Remove step"
              tabIndex={0}
            >
              <KuikaSvg name="trash" width={14} height={14} />
            </KuikaButton>
          </div>
        </div>
        <div className="onboarding-tooltip-modal__step-fields">
          <div className="onboarding-tooltip-modal__field">
            <KuikaSmallText>Selector</KuikaSmallText>
            <KuikaInput
              value={step.selector || ""}
              onChange={(_, value) => {
                const updatedSteps = formValues.steps.map((s: IOnboardingStep, i: number) =>
                  i === index ? { ...s, selector: String(value) } : s
                );
                updateFormValue("steps", updatedSteps);
              }}
              placeholder={isFirst && selector ? selector : "data-onboarding-id or CSS selector"}
              disabled={isSelectorDisabled}
            />
            {isSelectorDisabled && (
              <KuikaSmallText className="onboarding-tooltip-modal__hint">
                First step inherits selector from tooltip
              </KuikaSmallText>
            )}
          </div>
          <div className="onboarding-tooltip-modal__field">
            <KuikaSmallText>Header</KuikaSmallText>
            <KuikaInput
              value={step.header || ""}
              onChange={(_, value) => {
                const updatedSteps = formValues.steps.map((s: IOnboardingStep, i: number) =>
                  i === index ? { ...s, header: String(value) } : s
                );
                updateFormValue("steps", updatedSteps);
              }}
              placeholder="Step header"
            />
          </div>
          <div className="onboarding-tooltip-modal__field">
            <KuikaSmallText>Label</KuikaSmallText>
            <KuikaInput
              type="textarea"
              value={step.label || ""}
              onChange={(_, value) => {
                const updatedSteps = formValues.steps.map((s: IOnboardingStep, i: number) =>
                  i === index ? { ...s, label: String(value) } : s
                );
                updateFormValue("steps", updatedSteps);
              }}
              placeholder="Step description"
              rows={2}
            />
          </div>
          <div className="onboarding-tooltip-modal__field">
            <KuikaSmallText>Image</KuikaSmallText>
            <div className="onboarding-tooltip-modal__image-upload">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleStepImageUpload(index, e)}
                style={{ display: "none" }}
                id={`step-image-upload-${index}`}
              />
              <KuikaButton
                type="secondary"
                onClick={() => document.getElementById(`step-image-upload-${index}`)?.click()}
              >
                <KuikaSvg name="upload" width={14} height={14} />
                Upload Image (Max 5MB)
              </KuikaButton>
              {step.image && (
                <div className="onboarding-tooltip-modal__image-preview">
                  <img
                    src={getImageSrc(step.image)}
                    alt="Step preview"
                    style={{ maxWidth: "100px", maxHeight: "60px" }}
                  />
                  <KuikaButton
                    type="text"
                    isIcon
                    color="danger"
                    onClick={() => {
                      const updatedSteps = formValues.steps.map((s: IOnboardingStep, i: number) =>
                        i === index ? { ...s, image: "" } : s
                      );
                      updateFormValue("steps", updatedSteps);
                    }}
                    aria-label="Remove image"
                    tabIndex={0}
                  >
                    <KuikaSvg name="trash" width={14} height={14} />
                  </KuikaButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    const previewSteps = showStepsEditor && steps.length > 0 ? steps : undefined;

    const renderIcon = () => {
      if (!icon) return null;

      return (
        <div
          className={`onboarding-tooltip-modal__preview-icon onboarding-tooltip-modal__preview-icon--${iconPosition}`}
          style={{ color: iconColor || "var(--blue-6)" }}
        >
          {icon.startsWith("animated-") ? (
            <OnboardingAnimatedIcon
              type={icon.replace("animated-", "") as AnimatedIconType}
              size={iconSize ? parseInt(iconSize, 10) : 20}
              color={iconColor || "var(--blue-6)"}
            />
          ) : (
            <KuikaSvg
              name={icon}
              type="common"
              width={iconSize ? parseInt(iconSize, 10) : 20}
              height={iconSize ? parseInt(iconSize, 10) : 20}
              color={iconColor || "var(--blue-6)"}
            />
          )}
        </div>
      );
    };

    const tooltipProps = {
      key: `${triggerType}-${icon}-${iconPosition}`, // Re-mount when critical props change to reset state
      label: !previewSteps ? label : undefined,
      header: !previewSteps ? header : undefined,
      image: !previewSteps ? image : undefined,
      steps: previewSteps,
      learnMoreUrl: learnMoreUrl || undefined,
      position,
      backgroundColor: backgroundColor || undefined,
      textColor: textColor || undefined,
      width: width ? parseInt(width, 10) : undefined,
      height: height ? parseInt(height, 10) : undefined,
      triggerType,
      isInModal: true
    };

    const targetButton = <KuikaButton type="secondary">Preview Target</KuikaButton>;

    return (
      <div className="onboarding-tooltip-modal__preview">
        <KuikaSmallMediumText>Preview</KuikaSmallMediumText>
        <div className="onboarding-tooltip-modal__preview-content">
          <div className="onboarding-tooltip-modal__preview-container">
            {!icon ? (
              <KuikaOnboardingTooltip {...tooltipProps}>{targetButton}</KuikaOnboardingTooltip>
            ) : (
              <>
                {targetButton}
                <KuikaOnboardingTooltip {...tooltipProps}>{renderIcon()}</KuikaOnboardingTooltip>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="onboarding-tooltip-modal__layout">
        <div className="onboarding-tooltip-modal__content">
          <div className="onboarding-tooltip-modal__section">
            <Form.Item name="name" noStyle>
              <div className="onboarding-tooltip-modal__field">
                <KuikaSmallText>Name</KuikaSmallText>
                <KuikaInput
                  data-testid="onboardingTooltipNameInput"
                  value={formValues.name}
                  onChange={(_, value) => updateFormValue("name", String(value))}
                  placeholder="Tooltip name"
                />
                <KuikaSmallText className="onboarding-tooltip-modal__hint">
                  Optional name to identify this tooltip
                </KuikaSmallText>
              </div>
            </Form.Item>
            <div className="onboarding-tooltip-modal__field">
              <div className="onboarding-tooltip-modal__field-header">
                <KuikaSmallText>Selector</KuikaSmallText>
                <Form.Item name="useCssSelector" noStyle valuePropName="checked">
                  <KuikaSwitch
                    checked={useCssSelector}
                    onChange={() => updateFormValue("useCssSelector", !useCssSelector)}
                    label="Use CSS Selector"
                    labelPosition="left"
                    align="center"
                  />
                </Form.Item>
              </div>
              <Form.Item name="selector" noStyle>
                <KuikaInput
                  data-testid="onboardingTooltipSelectorInput"
                  value={selector}
                  onChange={(_, value) => updateFormValue("selector", String(value))}
                  placeholder={useCssSelector ? ".class-name or #element-id" : "header_toolbar_datasources"}
                />
              </Form.Item>
              <KuikaSmallText className="onboarding-tooltip-modal__hint">
                {useCssSelector
                  ? "Enter a valid CSS selector (e.g., .my-class, #my-id, div.container)"
                  : "The data-onboarding-id attribute value"}
              </KuikaSmallText>
            </div>
          </div>

          <div className="onboarding-tooltip-modal__section">
            <div className="onboarding-tooltip-modal__section-header">
              <KuikaMediumText>Content</KuikaMediumText>
              <KuikaButton type="text" onClick={handleToggleStepsEditor}>
                {showStepsEditor ? "Use Simple Content" : "Use Steps"}
              </KuikaButton>
            </div>

            {!showStepsEditor ? (
              <>
                <Form.Item name="header" noStyle>
                  <div className="onboarding-tooltip-modal__field">
                    <KuikaSmallText>Header</KuikaSmallText>
                    <KuikaInput
                      data-testid="onboardingTooltipHeaderInput"
                      value={header}
                      onChange={(_, value) => updateFormValue("header", String(value))}
                      placeholder="Tooltip header"
                    />
                  </div>
                </Form.Item>
                <Form.Item name="label" noStyle>
                  <div className="onboarding-tooltip-modal__field">
                    <KuikaSmallText>Label</KuikaSmallText>
                    <KuikaInput
                      data-testid="onboardingTooltipLabelInput"
                      type="textarea"
                      value={label}
                      onChange={(_, value) => updateFormValue("label", String(value))}
                      placeholder="Tooltip description"
                      rows={2}
                    />
                  </div>
                </Form.Item>
                <Form.Item name="image" noStyle>
                  <div className="onboarding-tooltip-modal__field">
                    <KuikaSmallText>Image</KuikaSmallText>
                    <div className="onboarding-tooltip-modal__image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: "none" }}
                        id="main-image-upload"
                      />
                      <KuikaButton
                        type="secondary"
                        onClick={() => document.getElementById("main-image-upload")?.click()}
                      >
                        <KuikaSvg name="upload" width={14} height={14} />
                        Upload Image (Max 5MB)
                      </KuikaButton>
                      {image && (
                        <div className="onboarding-tooltip-modal__image-preview">
                          <img
                            src={getImageSrc(image)}
                            alt="Preview"
                            style={{ maxWidth: "148px", maxHeight: "80px" }}
                          />
                          <KuikaButton
                            type="text"
                            isIcon
                            color="danger"
                            onClick={() => updateFormValue("image", "")}
                            aria-label="Remove image"
                            tabIndex={0}
                          >
                            <KuikaSvg name="trash" width={14} height={14} />
                          </KuikaButton>
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Item>
              </>
            ) : (
              <Form.Item name="steps" noStyle>
                <div className="onboarding-tooltip-modal__steps">
                  {steps.map((step: IOnboardingStep, index: number) => renderStepEditor(step, index))}
                  <KuikaButton type="secondary" onClick={handleAddStep}>
                    <KuikaSvg name="plus" width={14} height={14} />
                    Add Step
                  </KuikaButton>
                </div>
              </Form.Item>
            )}
          </div>

          <div className="onboarding-tooltip-modal__section">
            <KuikaMediumText>Behavior</KuikaMediumText>
            <div className="onboarding-tooltip-modal__row">
              <Form.Item name="position" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Position</KuikaSmallText>
                  <KuikaSelectbox
                    data-testid="onboardingTooltipPositionSelect"
                    value={position}
                    onChange={(value) => updateFormValue("position", value || "bottom")}
                    options={[
                      { value: "top", label: "Top" },
                      { value: "right", label: "Right" },
                      { value: "bottom", label: "Bottom" },
                      { value: "left", label: "Left" }
                    ]}
                  />
                </div>
              </Form.Item>
              <Form.Item name="triggerType" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Trigger Type</KuikaSmallText>
                  <KuikaSelectbox
                    data-testid="onboardingTooltipTriggerSelect"
                    value={triggerType}
                    onChange={(value) => updateFormValue("triggerType", value || "hover")}
                    options={[
                      { value: "hover", label: "Hover" },
                      { value: "click", label: "Click" },
                      { value: "hint", label: "Hint" },
                      { value: "always", label: "Always Visible" }
                    ]}
                  />
                </div>
              </Form.Item>
            </div>
            <div className="onboarding-tooltip-modal__row">
              <Form.Item name="tooltipOffsetX" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Tooltip Offset X (px)</KuikaSmallText>
                  <KuikaInput
                    type="number"
                    value={formValues.tooltipOffsetX}
                    onChange={(_, value) => updateFormValue("tooltipOffsetX", String(value))}
                    placeholder="0"
                  />
                </div>
              </Form.Item>
              <Form.Item name="tooltipOffsetY" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Tooltip Offset Y (px)</KuikaSmallText>
                  <KuikaInput
                    type="number"
                    value={formValues.tooltipOffsetY}
                    onChange={(_, value) => updateFormValue("tooltipOffsetY", String(value))}
                    placeholder="0"
                  />
                </div>
              </Form.Item>
            </div>
          </div>

          <div className="onboarding-tooltip-modal__section">
            <KuikaMediumText>Icon Trigger</KuikaMediumText>
            <KuikaSmallText className="onboarding-tooltip-modal__hint">
              When an icon is selected, it will be displayed near the element. The tooltip appears on hover/click of the
              icon.
            </KuikaSmallText>
            <Form.Item name="icon" noStyle>
              <div className="onboarding-tooltip-modal__field">
                <KuikaSmallText>Icon</KuikaSmallText>
                <KuikaSelectbox
                  value={icon}
                  onChange={(value) => updateFormValue("icon", value || "")}
                  options={[
                    { value: "", label: "None (Direct tooltip)" },
                    ...ONBOARDING_ICON_OPTIONS.filter((opt) => opt.value.startsWith("animated-")),
                    ...ONBOARDING_ICON_OPTIONS.filter((opt) => !opt.value.startsWith("animated-"))
                  ]}
                  placeholder="Select an icon..."
                />
              </div>
            </Form.Item>
            {icon && (
              <>
                <div className="onboarding-tooltip-modal__icon-preview">
                  <KuikaSmallText>Icon Preview:</KuikaSmallText>
                  <div
                    className="onboarding-tooltip-modal__icon-preview-box"
                    style={{ color: iconColor || "var(--blue-6)" }}
                  >
                    {icon.startsWith("animated-") ? (
                      <OnboardingAnimatedIcon
                        type={icon.replace("animated-", "") as AnimatedIconType}
                        size={iconSize ? parseInt(iconSize, 10) : 20}
                        color={iconColor || "var(--blue-6)"}
                      />
                    ) : (
                      <KuikaSvg
                        name={icon}
                        type="common"
                        width={iconSize ? parseInt(iconSize, 10) : 20}
                        height={iconSize ? parseInt(iconSize, 10) : 20}
                        color={iconColor || "var(--blue-6)"}
                      />
                    )}
                  </div>
                </div>
                <div className="onboarding-tooltip-modal__row">
                  <Form.Item name="iconColor" noStyle>
                    <div className="onboarding-tooltip-modal__field">
                      <KuikaSmallText>Icon Color</KuikaSmallText>
                      <KuikaColorPicker
                        color={iconColor || "#246EFF"}
                        onChange={(color) => updateFormValue("iconColor", color)}
                      />
                    </div>
                  </Form.Item>
                  <Form.Item name="iconSize" noStyle>
                    <div className="onboarding-tooltip-modal__field">
                      <KuikaSmallText>Icon Size (px)</KuikaSmallText>
                      <KuikaInput
                        type="number"
                        value={iconSize}
                        onChange={(_, value) => updateFormValue("iconSize", String(value))}
                        placeholder="20"
                      />
                    </div>
                  </Form.Item>
                  <IconPositionSelector
                    value={iconPosition}
                    onChange={(value) => updateFormValue("iconPosition", value)}
                  />
                </div>
                <div className="onboarding-tooltip-modal__row">
                  <Form.Item name="iconOffsetX" noStyle>
                    <div className="onboarding-tooltip-modal__field">
                      <KuikaSmallText>Icon Offset X (px)</KuikaSmallText>
                      <KuikaInput
                        type="number"
                        value={formValues.iconOffsetX}
                        onChange={(_, value) => updateFormValue("iconOffsetX", String(value))}
                        placeholder="0"
                      />
                    </div>
                  </Form.Item>
                  <Form.Item name="iconOffsetY" noStyle>
                    <div className="onboarding-tooltip-modal__field">
                      <KuikaSmallText>Icon Offset Y (px)</KuikaSmallText>
                      <KuikaInput
                        type="number"
                        value={formValues.iconOffsetY}
                        onChange={(_, value) => updateFormValue("iconOffsetY", String(value))}
                        placeholder="0"
                      />
                    </div>
                  </Form.Item>
                </div>
              </>
            )}
          </div>

          <div className="onboarding-tooltip-modal__section">
            <KuikaMediumText>Appearance</KuikaMediumText>
            <div className="onboarding-tooltip-modal__row">
              <Form.Item name="backgroundColor" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Background Color</KuikaSmallText>
                  <KuikaColorPicker
                    color={backgroundColor || "#f2f7ff"}
                    onChange={(color) => updateFormValue("backgroundColor", color)}
                  />
                </div>
              </Form.Item>
              <Form.Item name="textColor" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Text Color</KuikaSmallText>
                  <KuikaColorPicker
                    color={textColor || "#122c4b"}
                    onChange={(color) => updateFormValue("textColor", color)}
                  />
                </div>
              </Form.Item>
            </div>
            <div className="onboarding-tooltip-modal__row">
              <Form.Item name="width" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Width (px)</KuikaSmallText>
                  <KuikaInput
                    type="number"
                    value={width}
                    onChange={(_, value) => updateFormValue("width", String(value))}
                    placeholder="Auto"
                  />
                </div>
              </Form.Item>
              <Form.Item name="height" noStyle>
                <div className="onboarding-tooltip-modal__field">
                  <KuikaSmallText>Height (px)</KuikaSmallText>
                  <KuikaInput
                    type="number"
                    value={height}
                    onChange={(_, value) => updateFormValue("height", String(value))}
                    placeholder="Auto"
                  />
                </div>
              </Form.Item>
            </div>
          </div>

          <div className="onboarding-tooltip-modal__section">
            <KuikaMediumText>Actions</KuikaMediumText>
            <Form.Item name="learnMoreUrl" noStyle>
              <div className="onboarding-tooltip-modal__field">
                <KuikaSmallText>Learn More URL</KuikaSmallText>
                <KuikaInput
                  value={learnMoreUrl}
                  onChange={(_, value) => updateFormValue("learnMoreUrl", String(value))}
                  placeholder="https://docs.example.com/topic"
                />
              </div>
            </Form.Item>
          </div>

          <div className="onboarding-tooltip-modal__section">
            <KuikaMediumText>Status</KuikaMediumText>
            <Form.Item name="isDraft" noStyle valuePropName="checked">
              <KuikaSwitch
                dataTestId="onboardingTooltipDraftSwitch"
                checked={formValues.isDraft}
                onChange={() => updateFormValue("isDraft", !formValues.isDraft)}
                label="Draft Mode"
                labelPosition="left"
                align="center"
              />
            </Form.Item>
            <KuikaSmallText className="onboarding-tooltip-modal__hint">
              Draft tooltips are not visible to users
            </KuikaSmallText>
          </div>
        </div>
        {renderPreview()}
      </div>
    </>
  );
};

/** What each commit is doing, rather than the generic "Saving". */
const CREATE_TOOLTIP_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };
const UPDATE_TOOLTIP_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Updating", success: "Updated" };

export const OnboardingTooltipModal: React.FC<IOnboardingTooltipModalProps> = ({
  isOpen,
  onClose,
  tooltip,
  folderId,
  refreshOnboardingTooltips
}) => {
  const [form] = Form.useForm<IFormValues>();
  const formValuesRef = React.useRef<IFormValues | null>(null);
  // Where the save stands. Owned by `SaveStatus`, never set directly.
  const [saveStatus, setSaveStatus] = React.useState<KuikaButtonStatus>("idle");
  const saveMachineRef = React.useRef<SaveStatus | null>(null);
  if (saveMachineRef.current === null) {
    saveMachineRef.current = new SaveStatus(setSaveStatus);
  }
  const saveMachine: SaveStatus = saveMachineRef.current;
  React.useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);
  // The ✓ hold counts as busy too: the dialog is about to leave on its own.
  const isBusy: boolean = saveStatus === "loading" || saveStatus === "success";

  const handleSave = async () => {
    // A second press while the tooltip is saved or its ✓ is held would save it twice.
    if (!saveMachine.canStart) return;
    saveMachine.start();
    try {
      const values = formValuesRef.current || form.getFieldsValue(true);

      const hasSteps = values.showStepsEditor && values.steps.length > 0;
      const steps = hasSteps
        ? values.steps.map((step: IOnboardingStep, index: number) =>
            index === 0 ? { ...step, selector: values.selector } : step
          )
        : [];

      const tooltipData: Partial<IOnboardingTooltipItem> = {
        id: tooltip?.id,
        name: values.name || undefined,
        selector: values.selector || undefined,
        useCssSelector: values.useCssSelector || undefined,
        label: values.label || undefined,
        header: values.header || undefined,
        image: values.image || undefined,
        steps: values.showStepsEditor && steps.length > 0 ? steps : undefined,
        learnMoreUrl: values.learnMoreUrl || undefined,
        position: values.position || "bottom",
        triggerType: values.triggerType || "hover",
        backgroundColor: values.backgroundColor || undefined,
        textColor: values.textColor || undefined,
        width: values.width ? parseInt(values.width, 10) : undefined,
        height: values.height ? parseInt(values.height, 10) : undefined,
        icon: values.icon || undefined,
        iconColor: values.iconColor || undefined,
        iconSize: values.iconSize ? parseInt(values.iconSize, 10) : undefined,
        iconPosition: (values.iconPosition as OnboardingIconPosition) || undefined,
        tooltipOffsetX: values.tooltipOffsetX ? parseInt(values.tooltipOffsetX, 10) : undefined,
        tooltipOffsetY: values.tooltipOffsetY ? parseInt(values.tooltipOffsetY, 10) : undefined,
        iconOffsetX: values.iconOffsetX ? parseInt(values.iconOffsetX, 10) : undefined,
        iconOffsetY: values.iconOffsetY ? parseInt(values.iconOffsetY, 10) : undefined,
        isDraft: values.isDraft ?? true,
        folderId: tooltip?.folderId || folderId
      };

      await OnboardingTooltipService.saveTooltip(tooltipData);

      if (refreshOnboardingTooltips) {
        refreshOnboardingTooltips();
      }

      // The ✓ goes up first; the dialog leaves after it and the panel reloads its tooltip list.
      saveMachine.succeed(onClose);
    } catch (error) {
      console.error("Failed to save tooltip:", error);
      saveMachine.fail();
    }
  };

  /** Cancel, the X and Escape. During the ✓ hold the dialog is already on its way out. */
  const handleClose = (): void => {
    if (saveMachine.current === "success") return;
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        name: tooltip?.name || "",
        selector: tooltip?.selector || "",
        useCssSelector: tooltip?.useCssSelector || false,
        label: tooltip?.label || "",
        header: tooltip?.header || "",
        image: tooltip?.image || "",
        steps: tooltip?.steps || [],
        learnMoreUrl: tooltip?.learnMoreUrl || "",
        position: tooltip?.position || "bottom",
        triggerType: tooltip?.triggerType || "hover",
        backgroundColor: tooltip?.backgroundColor || "",
        textColor: tooltip?.textColor || "",
        width: tooltip?.width?.toString() || "",
        height: tooltip?.height?.toString() || "",
        icon: tooltip?.icon || "",
        iconColor: tooltip?.iconColor || "",

        iconSize: tooltip?.iconSize?.toString() || "",
        iconPosition: tooltip?.iconPosition || "right-center",
        isDraft: tooltip?.isDraft ?? true,
        showStepsEditor: (tooltip?.steps?.length ?? 0) > 0
      });
    }
  }, [isOpen, tooltip, form]);

  const isEdit = !!tooltip;

  return (
    <KModal
      showModal={isOpen}
      closeModal={handleClose}
      header={isEdit ? "Edit Tooltip" : "Create Tooltip"}
      width={800}
      className="onboarding-tooltip-modal"
      footer={
        <KModalFooter
          onCancel={handleClose}
          cancelTestId="onboardingTooltipCancelButton"
          submitLabel={isEdit ? "Update" : "Create"}
          submitTestId="onboardingTooltipSaveButton"
          onSubmit={handleSave}
          status={saveStatus}
          statusLabels={isEdit ? UPDATE_TOOLTIP_STATUS_LABELS : CREATE_TOOLTIP_STATUS_LABELS}
        />
      }
    >
      <Form form={form} layout="vertical" className="onboarding-tooltip-modal__form">
        <OnboardingTooltipModalContent
          form={form}
          tooltip={tooltip}
          onClose={onClose}
          folderId={folderId}
          refreshOnboardingTooltips={refreshOnboardingTooltips}
          formValuesRef={formValuesRef}
        />
      </Form>
    </KModal>
  );
};
